/**
 * AgentesPorEtapa — seção da Auditoria de Contexto (SPEC A3 FR-21).
 *
 * Por etapa: o agente que atuou, a versão do prompt, o tier, o tamanho de cada bloco do
 * prompt efetivo (especialista, skills, memória, tarefa, regras) e, em destaque, os cortes
 * (bloco, de → para caracteres) e os conflitos com as regras da plataforma.
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { C } from '@/theme/palette';
import { Card, Badge } from '@/components/ui';
import {
  inteiro, rotuloDaEtapa, rotuloDaParte, partesOrdenadas, lista,
} from '@/lib/agentesDaExecucao';

function Cortes({ cortes }) {
  if (!cortes.length) return null;
  return (
    <div
      role="alert"
      data-testid="cortes-da-etapa"
      style={{
        marginTop: 8, padding: '8px 10px', borderRadius: 6,
        background: `${C.bad}14`, border: `1px solid ${C.bad}55`,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: C.bad, marginBottom: 4 }}>
        {cortes.length} corte(s) no prompt
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.neutral, lineHeight: 1.6 }}>
        {cortes.map((c, i) => (
          <li key={i}>
            {rotuloDaParte(c.parte)}: {inteiro(c.de)} → {inteiro(c.para)} ch
          </li>
        ))}
      </ul>
    </div>
  );
}

function Conflitos({ conflitos }) {
  if (!conflitos.length) return null;
  return (
    <div style={{
      marginTop: 8, padding: '8px 10px', borderRadius: 6,
      background: `${C.warn}14`, border: `1px solid ${C.warn}55`,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.warn, marginBottom: 4 }}>
        Conflito(s) com as regras da plataforma
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.neutral, lineHeight: 1.6 }}>
        {conflitos.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

/** @param {{ agentes: Array<object>|undefined }} props — `trace.agentes` (§6). */
export function AgentesPorEtapa({ agentes }) {
  const itens = lista(agentes);
  if (!itens.length) return null;
  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Agentes por etapa</div>
      <div style={{ color: C.neutralMuted, fontSize: 12, marginBottom: 8 }}>
        Quem atuou em cada etapa, com qual versão de prompt e quanto de cada bloco chegou ao
        modelo. Corte = bloco que não coube e foi reduzido.
      </div>
      {itens.map((a, i) => {
        const cortes = lista(a.cortes);
        const conflitos = (Array.isArray(a.conflitos) ? a.conflitos : []).filter(Boolean);
        const partes = partesOrdenadas(a.partes);
        return (
          <div
            key={`${a.etapa}-${a.agente}-${i}`}
            data-testid="agente-da-etapa"
            style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                          fontSize: 13 }}>
              <strong>{rotuloDaEtapa(a.etapa)}</strong>
              <span style={{ color: C.neutralMuted }}>·</span>
              <span>{a.agente || '—'}</span>
              <Badge color={C.cyber} light>{a.tier || '—'}</Badge>
              <span style={{ color: C.neutralMuted, fontSize: 12 }}>
                prompt {a.version_id ? `versão ${a.version_id}` : 'sem versão (do catálogo)'}
              </span>
            </div>
            {partes.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6,
                            fontSize: 12, color: C.neutralDim }}>
                {partes.map(([k, v]) => (
                  <span key={k}>
                    {rotuloDaParte(k)}: <span style={{ color: C.text }}>{inteiro(v)} ch</span>
                  </span>
                ))}
              </div>
            ) : null}
            <Cortes cortes={cortes} />
            <Conflitos conflitos={conflitos} />
          </div>
        );
      })}
    </Card>
  );
}

export default AgentesPorEtapa;
